import axios from 'axios'
import Button from 'lib/components/Button/Button'
import Dialog from 'lib/components/Dialog/Dialog'
import AuthContext from 'lib/context/AuthContext'
import useFocusOnMount from 'lib/hooks/useFocusOnMount'
import useOnInputChange from 'lib/hooks/useOnInputChange'
import createVideoSlug from 'lib/util/createVideoSlug'
import { reportError } from 'lib/util/errorHandler'
import isUniqueConstraintError from 'lib/util/isUniqueConstraintError'
import ActionDialog from 'pages/Home/admin/ActionDialog/ActionDialog'
import FormGroup from 'pages/Home/admin/FormGroup/FormGroup'
import VideoType from 'pages/Home/VideoType'
import React, { FormEvent, useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import getSlug from 'speakingurl'

export interface EditVideoDialogProps {
  onDeleteThumbnail: ()=> void
  onEditThumbnail: ()=> void
  onRequestClose: ()=> void
  video: VideoType
}

export default function EditVideoDialog(props: EditVideoDialogProps): JSX.Element {
  const { onDeleteThumbnail, onEditThumbnail, onRequestClose, video } = props
  const [t] = useTranslation()
  const inputEl = useRef(null)
  useFocusOnMount(inputEl)
  const [apiState, setApiState] = useState({ error: null, saving: false })
  const { getAccessToken } = useContext(AuthContext)
  const [state, setState] = useState({
    name: video.name,
    rise: video.rise,
    storyline: video.storyline,
    published: video.published,
    slug: video.slug,
    autoGenerateUrl: createVideoSlug(video.name, video.rise, video.storyline) === video.slug
  })
  const onInputChange = useOnInputChange<VideoType & { autoGenerateUrl: boolean }>(setState)

  const generatedUrl = createVideoSlug(state.name, state.rise, state.storyline)
  const slug = state.autoGenerateUrl ? generatedUrl : state.slug
  const showUrlGenerationWarning =
    state.autoGenerateUrl &&
    (state.name !== video.name || state.rise !== video.rise || state.storyline !== video.storyline) &&
    slug !== video.slug

  const validationError = (() => {
    const { name, published, rise, storyline } = state
    if (published) {
      if (!rise && !storyline) {
        return t('home.videosSection.admin.editDialog.categoryError')
      }
    }
    if (!name) {
      return t('home.videosSection.admin.editDialog.nameError')
    }
  })()

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (validationError) {
      return
    }
    setApiState({ saving: true, error: null })
    try {
      const accessToken = await getAccessToken()
      const headers = { Authorization: `Bearer ${accessToken}` }
      const data = { ...state, slug: getSlug(slug) }
      await axios.put(`${process.env.THREESIXTY_TRAINING_URI}/admin/videos/${video.id}`, data, { headers })
      window.location.reload()
    } catch (err) {
      if (isUniqueConstraintError(err)) {
        return setApiState({ saving: false, error: t('home.videosSection.admin.editDialog.uniqueError') })
      }
      setApiState({ saving: null, error: t('home.videosSection.admin.editDialog.apiError') })
      reportError(err, 'Error editing video', { videoId: video.id })
    }
  }

  function renderThumbnailSection() {
    const { thumbnailKey } = video
    const i18nPrefix = 'home.videosSection.admin.editDialog.thumbnail'

    return (
      <>
        <h4>{t(`${i18nPrefix}.customize`)}</h4>
        <Button type="button" variant="link" onClick={onEditThumbnail}>
          {thumbnailKey ? t(`${i18nPrefix}.replace`) : t(`${i18nPrefix}.upload`)}
        </Button>
        <br />
        {thumbnailKey && (
          <Button type="button" variant="link" onClick={onDeleteThumbnail}>
            {t(`${i18nPrefix}.delete`)}
          </Button>
        )}
      </>
    )
  }

  function renderUrlSection() {
    return (
      <div>
        <h4>{t('home.videosSection.admin.editDialog.url')}</h4>
        <FormGroup.Input
          label={t('home.videosSection.admin.editDialog.autoGenerate')}
          onChange={onInputChange}
          name="autoGenerateUrl"
          type="checkbox"
          checked={state.autoGenerateUrl}
        />
        <FormGroup.Input
          onChange={onInputChange}
          name="slug"
          placeholder={t('home.videosSection.admin.editDialog.path')}
          value={slug}
          disabled={state.autoGenerateUrl}
          notBlank
        />
      </div>
    )
  }

  const { error, saving } = apiState
  const errorMessage = error || validationError

  return (
    <Dialog className="edit-video-dialog" onRequestClose={onRequestClose}>
      <Dialog.Header onRequestClose={onRequestClose}>
        <Dialog.Title>{t('home.videosSection.admin.editDialog.header')}</Dialog.Title>
      </Dialog.Header>
      <form onSubmit={onSubmit}>
        <ActionDialog.Body>
          <FormGroup.Input
            ref={inputEl}
            onChange={onInputChange}
            name="name"
            placeholder={t('home.videosSection.admin.editDialog.placeholder')}
            value={state.name}
            notBlank
          />
          <h4>{t('home.videosSection.admin.editDialog.categories')}</h4>
          <FormGroup.Input
            label={t('productNames.rise')}
            onChange={onInputChange}
            name="rise"
            placeholder={t('productNames.rise')}
            type="checkbox"
            checked={state.rise}
          />
          <FormGroup.Input
            label={t('productNames.storyline')}
            onChange={onInputChange}
            name="storyline"
            placeholder={t('productNames.storyline')}
            type="checkbox"
            checked={state.storyline}
          />
          {renderUrlSection()}
          {renderThumbnailSection()}
          <h4>{t('home.videosSection.admin.editDialog.makePublic')}</h4>
          <FormGroup.Input
            label={t('home.videosSection.admin.editDialog.publish')}
            onChange={onInputChange}
            name="published"
            placeholder={t('home.videosSection.admin.editDialog.publish')}
            type="checkbox"
            checked={state.published}
          />
        </ActionDialog.Body>
        <ActionDialog.Footer>
          {showUrlGenerationWarning && (
            <ActionDialog.Warning>{t('home.videosSection.admin.editDialog.warning')}</ActionDialog.Warning>
          )}
          <div>
            <ActionDialog.CancelButton onClick={onRequestClose} />
            <ActionDialog.SaveButton loading={saving} />
          </div>
          <ActionDialog.Error message={errorMessage} />
        </ActionDialog.Footer>
      </form>
    </Dialog>
  )
}
